import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, Settings, Layers, Users, Users2, Milestone, Briefcase,
  FileUser, Newspaper, FileText, Image as ImageIcon, Mail, Handshake,
  ExternalLink, CheckCircle2, AlertTriangle, KeyRound, LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import AdminLogin from "@/admin/AdminLogin";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ResourceTab from "@/admin/ResourceTab";
import { CompanyTab, SettingsTab } from "@/admin/CompanySettings";
import { ApplicationsTab, MessagesTab } from "@/admin/InboxTabs";

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-navy-900 placeholder:text-slate-400 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";

const ARTICLE_CATEGORIES = ["Company News", "Education", "Academic Learning", "Music Education", "Leadership", "Technology", "Events", "Press Releases"];
const MEDIA_TYPES = ["Press Release", "News Coverage", "Interview", "Award", "Announcement", "Media Kit", "Corporate Download"];

const BRAND_FIELDS = [
  { key: "brandName", label: "Brand Name" },
  { key: "tagline", label: "Tagline" },
  { key: "category", label: "Category" },
  { key: "websiteURL", label: "Official Website URL" },
  { key: "brandColor", label: "Brand Colour (hex)", placeholder: "#059669" },
  { key: "displayOrder", label: "Display Order", type: "number" },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "purpose", label: "Purpose", type: "textarea", full: true },
  { key: "vision", label: "Vision", type: "textarea", full: true },
  { key: "heroImage", label: "Hero Image", type: "image", full: true },
  { key: "learningAreas", label: "Learning Areas (comma separated)", type: "list", full: true },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const LEADER_FIELDS = [
  { key: "fullName", label: "Full Name" },
  { key: "designation", label: "Designation" },
  { key: "organisation", label: "Organisation", placeholder: "Roy Career Solutions Private Limited" },
  { key: "brand", label: "Brand (if brand-level)", placeholder: "e.g. The Topper Academy" },
  { key: "leadershipCategory", label: "Category", type: "select", options: ["corporate", "brand"] },
  { key: "displayOrder", label: "Display Order", type: "number" },
  { key: "photograph", label: "Photograph", type: "image", full: true },
  { key: "shortBiography", label: "Short Biography", type: "textarea", full: true },
  { key: "fullBiography", label: "Full Biography", type: "textarea", full: true },
  { key: "education", label: "Education & Qualifications", type: "textarea", full: true },
  { key: "professionalBackground", label: "Professional Background", type: "textarea", full: true },
  { key: "expertise", label: "Areas of Expertise", type: "textarea", full: true },
  { key: "responsibilities", label: "Leadership Responsibilities", type: "textarea", full: true },
  { key: "achievements", label: "Achievements", type: "textarea", full: true },
  { key: "leadershipPhilosophy", label: "Leadership Philosophy", type: "textarea", full: true },
  { key: "quote", label: "Quote (publish only when approved)", type: "textarea", full: true },
  { key: "linkedinURL", label: "LinkedIn URL" },
  { key: "socialLinks", label: "Other Links" },
  { key: "featured", label: "Featured on Homepage", type: "checkbox", default: false, placeholder: "Show on homepage" },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const MILESTONE_FIELDS = [
  { key: "date", label: "Date", type: "date" },
  { key: "year", label: "Year", placeholder: "2024" },
  { key: "title", label: "Milestone Title", full: true },
  { key: "category", label: "Category", placeholder: "e.g. Brand Launch, Partnership, Award" },
  { key: "displayOrder", label: "Display Order", type: "number" },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const JOB_FIELDS = [
  { key: "title", label: "Job Title" },
  { key: "department", label: "Department" },
  { key: "brand", label: "Brand", placeholder: "RCS / The Topper Academy / Indian School of Music" },
  { key: "employmentType", label: "Employment Type", type: "select", options: ["Full-time", "Part-time", "Contract", "Internship"] },
  { key: "location", label: "Location" },
  { key: "workMode", label: "Work Mode", type: "select", options: ["Remote", "Hybrid", "Office"] },
  { key: "experience", label: "Experience Required" },
  { key: "description", label: "Role Description", type: "textarea", full: true },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const ARTICLE_FIELDS = [
  { key: "title", label: "Title", full: true },
  { key: "category", label: "Category", type: "select", options: ARTICLE_CATEGORIES },
  { key: "date", label: "Publish Date", type: "date" },
  { key: "author", label: "Author" },
  { key: "readingTime", label: "Reading Time", placeholder: "4 min read" },
  { key: "excerpt", label: "Excerpt", type: "textarea", full: true },
  { key: "body", label: "Article Body", type: "textarea", full: true },
  { key: "image", label: "Cover Image", type: "image", full: true },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const MEDIA_FIELDS = [
  { key: "title", label: "Title", full: true },
  { key: "type", label: "Type", type: "select", options: MEDIA_TYPES },
  { key: "date", label: "Date", type: "date" },
  { key: "url", label: "Link / File URL", full: true },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const GALLERY_FIELDS = [
  { key: "title", label: "Title" },
  { key: "category", label: "Category", placeholder: "e.g. Team, Events, Workshops" },
  { key: "displayOrder", label: "Display Order", type: "number" },
  { key: "image", label: "Image", type: "image", full: true },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const TEAM_FIELDS = [
  { key: "fullName", label: "Full Name" },
  { key: "designation", label: "Designation" },
  { key: "department", label: "Department", placeholder: "e.g. Digital Marketing" },
  { key: "linkedinURL", label: "LinkedIn URL" },
  { key: "displayOrder", label: "Display Order", type: "number" },
  { key: "photograph", label: "Photograph", type: "image", full: true },
  { key: "bio", label: "Profile / Bio", type: "textarea", full: true },
  { key: "isLead", label: "Team Lead", type: "checkbox", default: false, placeholder: "Displayed at the top of the department" },
  { key: "active", label: "Published", type: "checkbox", default: true },
];

const VERIFY_FIELDS = [
  ["legalName", "Company Legal Name"], ["cin", "CIN"], ["incorporationDate", "Incorporation Date"],
  ["registeredOffice", "Registered Office"], ["email", "Corporate Email"], ["phone", "Corporate Phone"],
];

function Dashboard() {
  const [counts, setCounts] = useState({});
  const [company, setCompany] = useState(null);

  useEffect(() => {
    const resources = ["brands", "leadership", "milestones", "jobs", "applications", "articles", "media", "gallery", "enquiries", "partnerships"];
    Promise.all(resources.map((r) => api.get(`/${r}?admin=true`).then((res) => [r, res.data.length]).catch(() => [r, 0])))
      .then((entries) => setCounts(Object.fromEntries(entries)));
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const missing = company ? VERIFY_FIELDS.filter(([k]) => !company[k]) : [];

  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-serif text-2xl text-navy-900">Dashboard</h2>
      <p className="mt-1 text-sm text-slate-500">Content overview for the RCS corporate website.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="dashboard-counts">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`dashboard-count-${k}`}>
            <p className="font-serif text-3xl text-navy-900">{v}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{k}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="verification-checklist">
        <h3 className="font-serif text-xl text-navy-900">Corporate Information Verification</h3>
        <p className="mt-1 text-xs text-slate-500">Complete and verify these details before public launch.</p>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {VERIFY_FIELDS.map(([k, label]) => {
            const done = company && company[k];
            return (
              <li key={k} className="flex items-center gap-3 text-sm" data-testid={`verify-${k}`}>
                {done ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
                )}
                <span className={done ? "text-slate-700" : "font-semibold text-amber-700"}>{label}</span>
              </li>
            );
          })}
        </ul>
        {missing.length > 0 && (
          <p className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800" data-testid="verification-warning">
            {missing.length} corporate field{missing.length > 1 ? "s" : ""} still need attention before the site can be considered verified.
          </p>
        )}
      </div>

      <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="admin-note">
        <h3 className="font-serif text-xl text-navy-900">Access Secured</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          This admin area is protected by secure sign-in. All content changes are restricted to
          authenticated administrators, with email-based password recovery available on the sign-in screen.
        </p>
      </div>
    </div>
  );
}

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, el: <Dashboard /> },
  { id: "company", label: "Company", icon: Building2, el: <CompanyTab /> },
  { id: "settings", label: "Site Settings", icon: Settings, el: <SettingsTab /> },
  { id: "brands", label: "Brands", icon: Layers, el: <ResourceTab resource="brands" title="Brands" testid="brands" columns={[{ key: "brandName", label: "Name" }, { key: "category", label: "Category" }, { key: "websiteURL", label: "Website" }, { key: "active", label: "Live" }]} fields={BRAND_FIELDS} /> },
  { id: "leadership", label: "Leadership", icon: Users, el: <ResourceTab resource="leadership" title="Leadership" testid="leadership" columns={[{ key: "fullName", label: "Name" }, { key: "designation", label: "Designation" }, { key: "brand", label: "Brand" }, { key: "featured", label: "Featured" }]} fields={LEADER_FIELDS} /> },
  { id: "team", label: "Team", icon: Users2, el: <ResourceTab resource="team" title="Team Members" testid="team" columns={[{ key: "fullName", label: "Name" }, { key: "designation", label: "Designation" }, { key: "department", label: "Department" }, { key: "isLead", label: "Lead" }]} fields={TEAM_FIELDS} /> },
  { id: "milestones", label: "Timeline", icon: Milestone, el: <ResourceTab resource="milestones" title="Milestones" testid="milestones" columns={[{ key: "year", label: "Year" }, { key: "title", label: "Title" }, { key: "category", label: "Category" }]} fields={MILESTONE_FIELDS} /> },
  { id: "jobs", label: "Jobs", icon: Briefcase, el: <ResourceTab resource="jobs" title="Jobs" testid="jobs" columns={[{ key: "title", label: "Title" }, { key: "department", label: "Department" }, { key: "brand", label: "Brand" }, { key: "active", label: "Live" }]} fields={JOB_FIELDS} /> },
  { id: "applications", label: "Applications", icon: FileUser, el: <ApplicationsTab /> },
  { id: "articles", label: "News", icon: Newspaper, el: <ResourceTab resource="articles" title="Articles" testid="articles" columns={[{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "date", label: "Date" }, { key: "active", label: "Live" }]} fields={ARTICLE_FIELDS} /> },
  { id: "media", label: "Media", icon: FileText, el: <ResourceTab resource="media" title="Media Items" testid="media" columns={[{ key: "title", label: "Title" }, { key: "type", label: "Type" }, { key: "date", label: "Date" }]} fields={MEDIA_FIELDS} /> },
  { id: "gallery", label: "Gallery", icon: ImageIcon, el: <ResourceTab resource="gallery" title="Gallery" testid="gallery" columns={[{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "active", label: "Live" }]} fields={GALLERY_FIELDS} /> },
  { id: "enquiries", label: "Enquiries", icon: Mail, el: <MessagesTab resource="enquiries" title="Enquiries" testid="enquiries" /> },
  { id: "partnerships", label: "Partnerships", icon: Handshake, el: <MessagesTab resource="partnerships" title="Partnership Enquiries" testid="partnerships" /> },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-password", { currentPassword: current, newPassword: next });
      toast.success("Password updated successfully.");
      onOpenChange(false);
      setCurrent("");
      setNext("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-navy-900">Change Admin Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2" data-testid="change-password-form">
          <div>
            <label htmlFor="cp-current" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Current Password</label>
            <input id="cp-current" type="password" required autoComplete="current-password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />
          </div>
          <div>
            <label htmlFor="cp-new" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">New Password (min 8 characters)</label>
            <input id="cp-new" type="password" required minLength={8} autoComplete="new-password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" />
          </div>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="cp-submit">
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApp() {
  const [tab, setTab] = useState("dashboard");
  const [auth, setAuth] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const active = TABS.find((t) => t.id === tab);

  useEffect(() => {
    const token = localStorage.getItem("rcs_admin_token");
    if (!token) {
      setAuth(false);
      return;
    }
    api.get("/auth/me")
      .then(() => setAuth(true))
      .catch(() => {
        localStorage.removeItem("rcs_admin_token");
        setAuth(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950 text-sm text-slate-400" data-testid="admin-auth-loading">
        Checking access…
      </div>
    );
  }
  if (auth === false) {
    return <AdminLogin onSuccess={() => setAuth(true)} />;
  }

  const logout = () => {
    localStorage.removeItem("rcs_admin_token");
    setAuth(false);
  };

  return (
    <div className="flex min-h-screen bg-ivory" data-testid="admin-app">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-navy-950" data-testid="admin-sidebar">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
          <img src="/rcs-logo.png" alt="Roy Career Solutions Private Limited" className="h-12 w-auto" data-testid="admin-logo" />
          <div className="leading-tight">
            <p className="font-serif text-sm font-semibold text-ivory">Admin CMS</p>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" aria-label="Admin sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-nav-${t.id}`}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[13px] font-semibold transition-colors ${
                tab === t.id ? "bg-gold/15 text-gold" : "text-slate-400 hover:bg-white/5 hover:text-ivory"
              }`}
            >
              <t.icon className="h-4 w-4 shrink-0" aria-hidden />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="space-y-3 border-t border-white/10 p-4">
          <Link to="/" className="flex items-center gap-2 text-xs font-semibold text-slate-400 transition-colors hover:text-gold" data-testid="admin-view-site">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden /> View Website
          </Link>
          <button onClick={() => setShowPw(true)} className="flex items-center gap-2 text-xs font-semibold text-slate-400 transition-colors hover:text-gold" data-testid="admin-change-password">
            <KeyRound className="h-3.5 w-3.5" aria-hidden /> Change Password
          </button>
          <button onClick={logout} className="flex items-center gap-2 text-xs font-semibold text-slate-400 transition-colors hover:text-red-400" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5" aria-hidden /> Sign Out
          </button>
        </div>
      </aside>

      <ChangePasswordDialog open={showPw} onOpenChange={setShowPw} />

      <div className="ml-60 flex-1 px-8 py-8 lg:px-12" data-testid="admin-main">
        {active.el}
      </div>
    </div>
  );
}
